import { Chip } from '@mui/material';
import HttpIcon from '@mui/icons-material/Http';
import NetworkPingIcon from '@mui/icons-material/NetworkPing';
import CableIcon from '@mui/icons-material/Cable';
import SearchIcon from '@mui/icons-material/Search';

/**
 * Monitor type matching the server-side MonitorType enum
 */
export type MonitorType = 'HTTP' | 'PING' | 'TCP' | 'KEYWORD';

/**
 * Type configuration with colors and icons
 */
const typeConfig: Record<
  MonitorType,
  {
    label: string;
    color: string;
    backgroundColor: string;
    icon: React.ReactElement;
  }
> = {
  HTTP: {
    label: 'HTTP',
    color: '#3B82F6',
    backgroundColor: 'rgba(59, 130, 246, 0.1)',
    icon: <HttpIcon sx={{ fontSize: 14 }} />,
  },
  PING: {
    label: 'Ping',
    color: '#8B5CF6',
    backgroundColor: 'rgba(139, 92, 246, 0.1)',
    icon: <NetworkPingIcon sx={{ fontSize: 14 }} />,
  },
  TCP: {
    label: 'TCP',
    color: '#F97316',
    backgroundColor: 'rgba(249, 115, 22, 0.1)',
    icon: <CableIcon sx={{ fontSize: 14 }} />,
  },
  KEYWORD: {
    label: 'Keyword',
    color: '#14B8A6',
    backgroundColor: 'rgba(20, 184, 166, 0.1)',
    icon: <SearchIcon sx={{ fontSize: 14 }} />,
  },
};

/**
 * MonitorTypeBadge component props
 */
interface MonitorTypeBadgeProps {
  type: MonitorType;
  size?: 'small' | 'medium';
  showIcon?: boolean;
}

/**
 * MonitorTypeBadge component
 * Displays a colored chip indicating monitor type with an icon
 */
export function MonitorTypeBadge({
  type,
  size = 'small',
  showIcon = true,
}: MonitorTypeBadgeProps) {
  const config = typeConfig[type];

  if (!config) {
    return null;
  }

  const chipProps = {
    label: config.label,
    size,
    ...(showIcon && { icon: config.icon }),
  };

  return (
    <Chip
      {...chipProps}
      sx={{
        color: config.color,
        backgroundColor: config.backgroundColor,
        fontWeight: 600,
        fontSize: size === 'small' ? '0.7rem' : '0.8rem',
        height: size === 'small' ? 24 : 28,
        '& .MuiChip-icon': {
          color: config.color,
          marginLeft: '6px',
        },
        '& .MuiChip-label': {
          px: showIcon ? 1 : 1.5,
        },
      }}
    />
  );
}

export default MonitorTypeBadge;
